import React, { memo } from 'react'
import styled from '@emotion/styled'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CrossIcon } from '../../../../assets/icons/app/cross.icon.svg'
import { ReactComponent as LeftChevronIcon } from '../../../../assets/icons/app/left-chevron.icon.svg'
import { ReactComponent as StarPopularityIcon } from '../../../../assets/icons/app/star-popularity.icon.svg'
import GlSpacer from '../../../share/GlSpacer'
import GlLoader from '../../../share/GlLoader'
import { getPopularityIndexByValue } from '../../../../core/enums/popularity.enum'
import { AuthorMirrorAuthorInterface } from '../../../../core/interface/report/author-mirror-author.interface'
import { AuthorInterface } from '../../../../core/interface/author.interface'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectAnalyticDetailsStateAuthorDetails,
  selectAnalyticDetailsStateIsLoadingGetAuthorDetails,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import { WorkMirrorBookInterface } from '../../../../core/interface/report/work-mirror-book.interface'
import { getInitial } from '../../../../core/utils/get-author-initials'
import ComputePercentageUtils from '../../../../core/utils/compute-percentage.utils'

const AuthorStyle = styled.div`
  color: #252525;
  font-variant-numeric: lining-nums proportional-nums;
  /* Body/B2 - Fira - Regular */
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`

const LabelStyle = styled.div`
  color: #3b3b3b;
  text-align: center;
  /* Caption/C1 - Medium - Uppercase */
  font-family: Fira Sans, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.24px;
  text-transform: uppercase;
  padding: 4px;
  background: #e9e9e9;
  border-radius: 4px;
`

const HeaderStyle = styled.div`
  color: #3b3b3b;
  font-variant-numeric: lining-nums proportional-nums;
  /* Body/B2 - Fira - Medium */
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
`

const AuthorCardStyle = styled.div`
  width: 100%;
  min-height: 48px;
  max-height: 48px;
  background: white;
  border-radius: 4px;
`

const AuthorMirrorAuthorDetailsCard = ({
  openListCard,
  handleBookSelected,
  handleAuthorSelected,
  authorDetailsSelected,
  handleCloseAuthorDetails,
  authorDetailsAuthorReco,
}: {
  openListCard: boolean
  handleBookSelected: (workMirror: WorkMirrorBookInterface | null) => void
  handleAuthorSelected: (
    authorMirror: AuthorMirrorAuthorInterface | null,
  ) => void
  authorDetailsSelected: AuthorMirrorAuthorInterface | null
  handleCloseAuthorDetails: (returnToList?: boolean) => void
  authorDetailsAuthorReco: AuthorInterface[]
}) => {
  const { t } = useTranslation()
  const onClickCloseButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    handleCloseAuthorDetails(true)
  }

  const onClickReturnToListButton = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    handleCloseAuthorDetails(false)
  }
  const authorDetails = useAppSelector(selectAnalyticDetailsStateAuthorDetails)

  const isLoadingGetAuthorDetails = useAppSelector(
    selectAnalyticDetailsStateIsLoadingGetAuthorDetails,
  )

  return (
    <div
      className="flex flex-col basis-1/3 gap-4 h-full"
      style={{
        maxWidth: 506,
      }}
    >
      <div className="flex flex-row items-center justify-between">
        {(openListCard && (
          <button
            type="button"
            className="rounded flex flex-row items-center justify-center gap-2 p-2 bg-[#e9e9e9]"
            onClick={onClickReturnToListButton}
          >
            <LeftChevronIcon className="w-4 h-4" />
            <span>{t('returnToList')}</span>
          </button>
        )) || <GlSpacer />}
        <button
          type="button"
          className="flex flex-row items-center justify-center gap-2 p-2 bg-[#e9e9e9] rounded"
          onClick={onClickCloseButton}
        >
          <CrossIcon className="w-4 h-4" />
          <span>{t('commons.buttons.close')}</span>
        </button>
      </div>
      <div className="relative flex-auto flex flex-col rounded-xl bg-white">
        <PerfectScrollbar
          className="flex-auto flex flex-col p-4 bg-white rounded-b-xl"
          style={{
            height: 0,
          }}
        >
          {(isLoadingGetAuthorDetails && (
            <div className="absolute w-full h-full flex items-center justify-center">
              <GlLoader />
            </div>
          )) || (
            <div className="mb-3 flex flex-row items-center gap-2">
              {authorDetails?.name && (
                <div
                  className="rounded bg-[#f7f7f7] flex flex-none items-center justify-center"
                  style={{
                    width: 40,
                    height: 40,
                  }}
                >
                  {getInitial(authorDetails.name)}
                </div>
              )}
              <AuthorStyle className="flex flex-row">
                {authorDetails?.name}
              </AuthorStyle>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {(!!authorDetailsSelected?.distance &&
              !!authorDetailsSelected?.popularity && (
                <div className="flex flex-row gap-4">
                  <LabelStyle className="flex basis-0 flex-1 min-w-0 items-center justify-between">
                    <div>{t('proximity')}</div>
                    <div>
                      {ComputePercentageUtils(
                        authorDetailsSelected?.distance || 0,
                      )}
                    </div>
                  </LabelStyle>

                  <LabelStyle className="flex basis-0 flex-1 min-w-0 items-center justify-between">
                    <div>{t('popularity')}</div>
                    <div className="flex flex-row items-center justify-center gap-2">
                      {Array(5)
                        .fill(null)
                        .map((_, index) => (
                          <StarPopularityIcon
                            key={`star-popularity-${index + 1}`}
                            style={{
                              color:
                                getPopularityIndexByValue(
                                  authorDetailsSelected?.popularity,
                                ) >= index
                                  ? '#252525'
                                  : '#bebebe',
                            }}
                          />
                        ))}
                    </div>
                  </LabelStyle>
                </div>
              )) ||
              null}
            {(!isLoadingGetAuthorDetails && authorDetailsAuthorReco?.length && (
              <div
                className="flex flex-col gap-4 py-2"
                style={{
                  maxWidth: 474,
                }}
              >
                <HeaderStyle>{t('worksByPopularity')}</HeaderStyle>
                <div>
                  {authorDetails?.works.map((work) => {
                    return (
                      <AuthorCardStyle
                        key={`similar-title-${work.id_ean}`}
                        className="flex flex-row m-1 gap-2"
                        onClick={() =>
                          handleBookSelected({
                            id_ean: work.id_ean,
                            popularity: 0,
                            book: work,
                          })
                        }
                        style={{
                          alignItems: 'center',
                          boxShadow:
                            ' 0 0 20px 0 rgba(0, 0, 0, 0.05), 0 2px 5px 0 rgba(0, 0, 0, 0.08)',
                        }}
                      >
                        <img
                          alt=""
                          src={work?.imgs}
                          style={{
                            width: '40px',
                            height: '40px',
                            margin: '4px',
                            objectFit: 'cover',
                            borderRadius: 2,
                          }}
                        />
                        <p>{work.title}</p>
                      </AuthorCardStyle>
                    )
                  })}
                </div>
              </div>
            )) ||
              null}
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  )
}

export default memo(AuthorMirrorAuthorDetailsCard)
